import { MapContainer, TileLayer, Marker, Popup, Polyline, CircleMarker, useMap, useMapEvents } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { useEffect } from "react";

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png",
  iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png",
});

const userIcon = new L.Icon({
  iconUrl: "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-blue.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png",
  iconSize: [25, 41], iconAnchor: [12, 41], popupAnchor: [1, -34],
});
const foodIcon = new L.Icon({
  iconUrl: "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-green.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png",
  iconSize: [25, 41], iconAnchor: [12, 41], popupAnchor: [1, -34],
});
const redIcon = new L.Icon({
  iconUrl: "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-red.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png",
  iconSize: [25, 41], iconAnchor: [12, 41], popupAnchor: [1, -34],
});
const orangeIcon = new L.Icon({
  iconUrl: "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-orange.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png",
  iconSize: [25, 41], iconAnchor: [12, 41], popupAnchor: [1, -34],
});

function MapClickHandler({ mapPickMode, onMapClick }) {
  useMapEvents({
    click(e) {
      if (mapPickMode && onMapClick) onMapClick({ lat: e.latlng.lat, lng: e.latlng.lng });
    }
  });
  return null;
}

function MapPickDragControl({ mapPickMode }) {
  const map = useMap();
  useEffect(() => {
    if (!map) return;
    if (mapPickMode) {
      map.dragging.disable();
    } else {
      map.dragging.enable();
    }
    return () => { map.dragging.enable(); };
  }, [map, mapPickMode]);
  return null;
}

function FlyToTarget({ target, onFlown }) {
  const map = useMap();
  useEffect(() => {
    if (target?.lat != null && target?.lng != null) {
      map.flyTo([target.lat, target.lng], 15);
      const t = setTimeout(() => onFlown?.(), 500);
      return () => clearTimeout(t);
    }
  }, [map, target?.lat, target?.lng, onFlown]);
  return null;
}

const dottedStyle = { color: "#6366f1", weight: 3, opacity: 0.8, dashArray: "8, 12", lineCap: "round" };

export default function FoodMap({ location, foods, onClaim, userId, activeDeliveries, mapPickMode, onMapClick, searchFlyTarget, onSearchFlown, searchCooldownUntil = 0 }) {
  const center = location ? [location.lat, location.lng] : [37.7749, -122.4194];
  const foodsWithLocation = foods.filter(f => f.lat && f.lng && !f.claimed);

  return (
    <div className={`h-full w-full ${mapPickMode ? "cursor-crosshair" : ""}`}>
      <MapContainer center={center} zoom={13} style={{ height: "100%", width: "100%" }} zoomControl={false}>
        <MapPickDragControl mapPickMode={!!mapPickMode} />
        <MapClickHandler mapPickMode={mapPickMode} onMapClick={onMapClick} />
        <FlyToTarget target={searchFlyTarget} onFlown={onSearchFlown} />
        <TileLayer attribution='&copy; OpenStreetMap' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
        {location && <Marker position={[location.lat, location.lng]} icon={userIcon}><Popup>You are here</Popup></Marker>}
        {foodsWithLocation.map(item => (
          <Marker key={item.id} position={[item.lat, item.lng]} icon={foodIcon}>
            <Popup>
              <div style={{ minWidth: "180px" }}>
                <img src={item.imageUrl || "/image192.png"} alt={item.name} style={{ width: "100%", height: "80px", objectFit: "cover", borderRadius: "8px", marginBottom: "8px" }} />
                <strong style={{ display: "block", marginBottom: "4px" }}>{item.name}</strong>
                <div style={{ fontSize: "12px", color: "#666" }}>Qty: {item.quantity} • {item.hoursLeft} hrs left</div>
                {item.address && <div style={{ fontSize: "12px", color: "#666" }}>{item.address}</div>}
                {item.hoursLeft < 3 && <span style={{ fontSize: "11px", color: "#dc2626", background: "#fef2f2", padding: "2px 8px", borderRadius: "12px" }}>Urgent</span>}
                {onClaim && item.userId !== userId && <button onClick={() => onClaim(item)} style={{ width: "100%", marginTop: "10px", padding: "7px", background: "#3b82f6", color: "white", border: "none", borderRadius: "8px", cursor: "pointer", fontSize: "13px", fontWeight: 700 }}>Claim</button>}
              </div>
            </Popup>
          </Marker>
        ))}
        {activeDeliveries && activeDeliveries.map(d => {
          if (!d.lat || !d.lng) return null;
          const routePoints = [];
          if (location) routePoints.push([location.lat, location.lng]);
          routePoints.push([d.lat, d.lng]);
          if (d.deliveryLat && d.deliveryLng) routePoints.push([d.deliveryLat, d.deliveryLng]);
          return (
            <span key={`route-${d.id}`}>
              {routePoints.length >= 2 && <Polyline positions={routePoints} pathOptions={dottedStyle} />}
              <Marker position={[d.lat, d.lng]} icon={orangeIcon}><Popup>Pickup: {d.name}</Popup></Marker>
              {d.deliveryLat && d.deliveryLng && (
                <>
                  <Marker position={[d.deliveryLat, d.deliveryLng]} icon={redIcon}><Popup>Drop-off: {d.name}</Popup></Marker>
                  <CircleMarker center={[d.deliveryLat, d.deliveryLng]} radius={12} pathOptions={{ color: "#dc2626", fillColor: "#dc2626", fillOpacity: 0.2, weight: 2, dashArray: "4, 4" }} />
                </>
              )}
            </span>
          );
        })}
      </MapContainer>
    </div>
  );
}
